import type { SpacingTypes } from '@etg/wings';
import { breakpoints, Inline } from '@etg/wings';
import { ArrowRight, ArrowsLeftRight } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { TripType } from '@eti/schema-types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useDirection, useTranslation } from '@eti/providers';
import type { OriginDestinationPerBoundType } from '../../utils';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';

const inlineSpacingStyles = (spacingY: number) => css`
  gap: ${spacingY}px 8px;
`;

interface RouteInfoWithSymbolsProps {
  bounds: OriginDestinationPerBoundType;
  'data-testid': string;
  journeyType: TripType;
  spacingY?: SpacingTypes;
}

const RouteInfoWithSymbols = ({
  bounds,
  'data-testid': dataTestId,
  journeyType,
  spacingY = 4,
}: RouteInfoWithSymbolsProps) => {
  const { isRTL } = useDirection();
  const { t } = useTranslation();

  const screenReaderRoutesText = useMemo(() => {
    switch (journeyType) {
      case TripType.OneWay:
        return `${t('ScreenReader.From.To.Text', bounds[0].origin, bounds[0].destination)}, ${t(
          'ScreenReader.OneWayTrip.AriaLabel.Text',
        )}`;
      case TripType.Return:
        return `${t('ScreenReader.From.To.Text', bounds[0].origin, bounds[0].destination)}, ${t(
          'ScreenReader.ReturnTrip.AriaLabel.Text',
        )}`;
      default:
        return `${bounds.reduce((acc: string, { origin, destination }, index: number) => {
          const comma = index === 0 ? '' : ', ';
          return acc + comma + t('ScreenReader.From.To.Text', origin, destination);
        }, '')}, ${t('ScreenReader.MultiStopTrip.AriaLabel.Text')}`;
    }
  }, [bounds, journeyType, t]);

  return (
    <>
      <span className={screenReadersOnlyStyles}>{screenReaderRoutesText}</span>
      <Inline
        aria-hidden="true"
        className={inlineSpacingStyles(spacingY)}
        collapseBelow={breakpoints._560}
        data-testid={`${dataTestId}-route-info`}
      >
        {bounds.reduce((acc: ReactNode[], { origin, destination }, index: number) => {
          if (journeyType !== TripType.Return || index === 0) {
            acc.push(
              <Inline alignY="center" key={`${origin}-${destination}-${index}`} spacing={8}>
                <bdi data-testid={`${dataTestId}-bound-${index}-origin`}>{origin}</bdi>
                {journeyType === TripType.MultiStop || journeyType === TripType.OneWay ? (
                  <ArrowRight
                    data-testid={`${dataTestId}-bound-${index}-arrow-right`}
                    mirrored={isRTL}
                  />
                ) : (
                  <ArrowsLeftRight
                    data-testid={`${dataTestId}-bound-${index}-arrow-left-right`}
                    mirrored={isRTL}
                  />
                )}
                <Inline noWrap spacing={0}>
                  <bdi data-testid={`${dataTestId}-bound-${index}-destination`}>{destination}</bdi>
                  {journeyType === TripType.MultiStop && index < bounds.length - 1 && <bdi>, </bdi>}
                </Inline>
              </Inline>,
            );
          }
          return acc;
        }, [])}
      </Inline>
    </>
  );
};

export default RouteInfoWithSymbols;
